import { useEffect, useState } from "react";
import axios from "axios";
import { apiInstanceFetch } from "../../../util/api";
import { setToast } from "../../../util/toast";
import { baseURL, secretKey } from "../../../util/config";
import dayjs from "dayjs";
import defaultImage from "../../../assets/images/default.jpg";

const PAGES = [
  { value: "shop", label: "Shop" },
  { value: "reels", label: "Reels" },
  { value: "faith", label: "Faith" },
  { value: "profile", label: "Profile" },
];

const PopupAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAds = () => {
    setLoading(true);
    apiInstanceFetch
      .get(`popupAd/adminList`)
      .then((res) => {
        if (res?.status) setAds(res.ads || []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const handleToggle = (ad) => {
    apiInstanceFetch
      .post(`popupAd/adminToggle`, { id: ad._id })
      .then((res) => {
        if (res?.status) {
          setToast("success", res.ad?.isActive ? "Ad turned ON" : "Ad turned OFF");
          setAds((prev) => prev.map((a) => (a._id === ad._id ? res.ad : a)));
        } else setToast("error", res?.message || "Failed");
      })
      .catch(() => setToast("error", "Failed"));
  };

  const handleDelete = (ad) => {
    if (!window.confirm(`Delete ad "${ad.title}"? This also resets all user dismissals.`)) return;
    apiInstanceFetch
      .post(`popupAd/adminDelete`, { id: ad._id })
      .then((res) => {
        if (res?.status) {
          setToast("success", "Ad deleted.");
          setAds((prev) => prev.filter((a) => a._id !== ad._id));
        } else setToast("error", res?.message || "Failed");
      })
      .catch(() => setToast("error", "Failed"));
  };

  const openEditor = (ad) => {
    setEditing(ad);
    setEditorOpen(true);
  };

  const closeEditor = (reload) => {
    setEditorOpen(false);
    setEditing(null);
    if (reload) fetchAds();
  };

  return (
    <div className="userMain">
      <div className="tableMain mt-3">
        <div className="d-flex justify-content-between align-items-center px-3 pt-2 pb-2 flex-wrap">
          <div>
            <h4>Popup Ads</h4>
            <small className="text-secondary">
              Banners shown to users on Shop, Reels, Faith, or Profile pages. Closing dismisses the ad until the user visits that page 3 more times; closing again hides it forever until a new ad is created.
            </small>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => openEditor(null)}>+ New ad</button>
        </div>
        <div className="px-3 table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Title</th>
                <th>Body</th>
                <th>Target Page</th>
                <th>CTA</th>
                <th>Active</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center text-secondary py-4">Loading…</td></tr>
              ) : ads.length === 0 ? (
                <tr><td colSpan="9" className="text-center text-secondary py-4">No popup ads yet.</td></tr>
              ) : (
                ads.map((ad, idx) => (
                  <tr key={ad._id}>
                    <td>{idx + 1}</td>
                    <td>
                      <img
                        src={ad.imageUrl || defaultImage}
                        alt=""
                        style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4 }}
                        onError={(e) => (e.currentTarget.src = defaultImage)}
                      />
                    </td>
                    <td>{ad.title}</td>
                    <td><small className="text-secondary">{(ad.body || "").substring(0, 60)}{ad.body?.length > 60 ? "…" : ""}</small></td>
                    <td><span className="badge bg-info text-capitalize">{ad.targetPage}</span></td>
                    <td>{ad.ctaLabel ? <small>{ad.ctaLabel}<br/><span className="text-secondary">{(ad.ctaUrl || "").substring(0, 30)}</span></small> : <span className="text-secondary">—</span>}</td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-sm ${ad.isActive ? "btn-success" : "btn-outline-secondary"}`}
                        onClick={() => handleToggle(ad)}
                      >
                        {ad.isActive ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td><small>{dayjs(ad.createdAt).format("DD MMM YYYY, HH:mm")}</small></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-outline-info" onClick={() => openEditor(ad)}>Edit</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(ad)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && (
        <PopupAdEditor ad={editing} onClose={closeEditor} />
      )}
    </div>
  );
};

// Modal — light theme to match the page background, NOT bg-dark.
const inputStyle = {
  background: "#fff",
  color: "#222",
  border: "1px solid #d6d3cc",
};
const labelStyle = { color: "#1a1614", fontWeight: 600, fontSize: 13 };
const helperStyle = { color: "#8a857f", fontSize: 11 };

const PopupAdEditor = ({ ad, onClose }) => {
  const [title, setTitle] = useState(ad?.title || "");
  const [body, setBody] = useState(ad?.body || "");
  const [targetPage, setTargetPage] = useState(ad?.targetPage || "shop");
  const [ctaLabel, setCtaLabel] = useState(ad?.ctaLabel || "");
  const [ctaUrl, setCtaUrl] = useState(ad?.ctaUrl || "");
  const [isActive, setIsActive] = useState(ad?.isActive ?? true);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !targetPage) {
      setToast("error", "Title and target page are required.");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("body", body.trim());
    fd.append("targetPage", targetPage);
    fd.append("ctaLabel", ctaLabel.trim());
    fd.append("ctaUrl", ctaUrl.trim());
    fd.append("isActive", isActive);
    if (image) fd.append("image", image);
    if (ad?._id) fd.append("id", ad._id);

    try {
      const url = `${baseURL}popupAd/${ad?._id ? "adminUpdate" : "adminCreate"}`;
      const headers = { key: secretKey, Authorization: sessionStorage.getItem("token") };
      const method = ad?._id ? axios.patch : axios.post;
      const res = await method(url, fd, { headers });
      if (res.data?.status) {
        setToast("success", ad?._id ? "Ad updated." : "Ad created.");
        onClose(true);
      } else {
        setToast("error", res.data?.message || "Failed.");
      }
    } catch (err) {
      setToast("error", err.message || "Failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={() => onClose(false)}
    >
      <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content" style={{ background: "#fffdf7", color: "#1a1614" }}>
          <form onSubmit={submit}>
            <div
              className="modal-header"
              style={{ borderBottom: "1px solid #e8e1d3", background: "#fffdf7", color: "#1a1614" }}
            >
              <h5 className="modal-title" style={{ color: "#1a1614", fontWeight: 600 }}>
                {ad?._id ? "Edit popup ad" : "New popup ad"}
              </h5>
              <button type="button" className="btn-close" onClick={() => onClose(false)}></button>
            </div>
            <div className="modal-body" style={{ background: "#fffdf7", color: "#1a1614" }}>
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>
                  Title <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={inputStyle}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={120}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>Body</label>
                <textarea
                  className="form-control"
                  style={inputStyle}
                  rows="3"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={500}
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label" style={labelStyle}>
                    Target page <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <select
                    className="form-select"
                    style={inputStyle}
                    value={targetPage}
                    onChange={(e) => setTargetPage(e.target.value)}
                  >
                    {PAGES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label d-block" style={labelStyle}>Active</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <label className="form-check-label" style={{ color: "#1a1614" }}>
                      {isActive ? "ON — users will see this ad" : "OFF — hidden from users"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>
                  Image (16:9 recommended, optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  style={inputStyle}
                  onChange={(e) => setImage(e.target.files?.[0])}
                />
                {ad?.imageUrl && !image && (
                  <div className="mt-2">
                    <img src={ad.imageUrl} alt="" style={{ maxWidth: 200, borderRadius: 6 }} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label" style={labelStyle}>CTA label (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={ctaLabel}
                    onChange={(e) => setCtaLabel(e.target.value)}
                    placeholder="Shop now"
                  />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label" style={labelStyle}>CTA URL (optional)</label>
                  <input
                    type="url"
                    className="form-control"
                    style={inputStyle}
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
              </div>
              <p style={helperStyle}>
                Closing dismisses the ad until the user visits that page 3 more times. Closing again hides it forever until you create a new ad for the same page.
              </p>
            </div>
            <div
              className="modal-footer"
              style={{ borderTop: "1px solid #e8e1d3", background: "#fffdf7" }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => onClose(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ background: "#1a1614", borderColor: "#1a1614" }}
              >
                {saving ? "Saving…" : ad?._id ? "Save changes" : "Create ad"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PopupAds;
